import {
  computed,
  nextTick,
  onMounted,
  ref,
  toValue,
  type ClassValue,
  type MaybeRefOrGetter,
  type ShallowRef,
  type StyleValue,
  type UnwrapRef,
} from 'vue';

import type { Message } from '@/shared/api';
import { useVirtualizer, type VirtualItem, type VirtualizerScrollToOptions } from '@/shared/lib';

const useMessagesListVirtualizer = <T extends Record<any, any> & { id: Message['id'] }>(
  _data: MaybeRefOrGetter<T[]>,
  element: ShallowRef<HTMLElement | null>,
) => {
  type EnrichedVirtualItem = VirtualItem & {
    key: string;
    item: T;
    style: StyleValue;
    class: ClassValue;
  };

  const ATTRIBUTE_NAME: string = 'data-message-id';

  const virtualReady = ref<boolean>(false);

  const data = computed<T[]>(() => toValue(_data));
  const dataCount = computed<number>(() => data.value.length);

  const getItemByIndex = (index: number): T | undefined => data.value[index];

  const getItemIndex = (messageId: Message['id']): number =>
    data.value.findIndex(({ id }: T): boolean => id === messageId);

  const virtualizer = useVirtualizer(
    computed<UnwrapRef<Parameters<typeof useVirtualizer>[0]>>(() => ({
      count: dataCount.value,

      getScrollElement: () => element.value,
      estimateSize: (index) =>
        element.value
          ?.querySelector(`[${ATTRIBUTE_NAME}="${getItemByIndex(index)?.id ?? ''}"]`)
          ?.getBoundingClientRect().height ?? 33,
      getItemKey: (index) => getItemByIndex(index)?.id ?? `message-index-${index}`,

      anchorTo: 'end',
      scrollEndThreshold: 80,
      followOnAppend: true,
      overscan: 5,
    })),
  );

  const enrichVirtualItem = (virtualItem: VirtualItem): EnrichedVirtualItem | null => {
    const item = getItemByIndex(virtualItem.index);
    if (!item) {
      return null;
    }

    return {
      ...virtualItem,
      key: virtualItem.key.toString(),
      item,
      style: { transform: `translateY(${virtualItem.start}px)` },
      class: 'absolute top-0 left-0 w-full',
    };
  };

  const virtualItems = computed<EnrichedVirtualItem[]>(() =>
    virtualizer.value
      .getVirtualItems()
      .map(enrichVirtualItem)
      .filter((item) => item !== null),
  );

  const totalSize = computed<number>(() => virtualizer.value.getTotalSize());
  const wrapperStyle = computed<StyleValue>(() => ({ height: `${totalSize.value}px` }));

  const getItemAttributes = (virtualItem: EnrichedVirtualItem) => ({
    'data-index': virtualItem.index,
    [ATTRIBUTE_NAME]: virtualItem.item.id,
  });

  const scrollToMessage = (messageId: Message['id']): void => {
    const messageIndex = getItemIndex(messageId);
    if (messageIndex === -1) {
      return;
    }

    virtualizer.value.scrollToIndex(messageIndex, { align: 'center' });
  };

  onMounted(() => {
    nextTick(() => {
      virtualizer.value.measure();
      virtualReady.value = true;
    });
  });

  return {
    virtualReady,
    virtualItems,
    wrapperStyle,
    isAtEnd: computed<boolean>(() => virtualizer.value.isAtEnd(100)),
    getItemAttributes,
    measure: (): void => virtualizer.value.measure(),
    measureElement: (el: Element | null) => virtualizer.value.measureElement(el),
    scrollToBottom: (options?: VirtualizerScrollToOptions): void => {
      const lastMessageIndex: number = dataCount.value - 1;
      if (lastMessageIndex < 0) {
        return;
      }

      virtualizer.value.scrollToIndex(lastMessageIndex, {
        ...options,
        align: 'end',
      });
    },
    scrollToMessage,
  };
};

export { useMessagesListVirtualizer };
